import { useCallback, useEffect, useRef, useState } from 'react';
import { searchGlobal, resolveGlobalSearchSelection, warmProjectSearchCandidates, resolveProjectSearchCandidateHints, type GlobalSearchPage, type GlobalSearchResult, type GlobalSearchType } from '../services/globalSearch';
import { currentGlobalSearchCandidateGeneration, getGlobalSearchCandidateIds, getGlobalSearchTaskCandidateIds, invalidateGlobalSearchCandidates } from '../services/globalSearchCandidates';
import { WORKSPACE_NAVIGATION_CHANGED_EVENT } from '../services/WorkspaceNavigationService';
import { subscribeToNavigationChanges } from '../services/WorkspaceNavigationLiveUpdates';
interface Options { actorId?: string | null; workspaceId?: string | null; query: string; enabled: boolean }
interface State { key: string; page: GlobalSearchPage | null; loading: boolean; error: string | null }
const unavailableMessage = 'Some results are temporarily unavailable.';
const failedMessage = 'Search is temporarily unavailable.';
/** Ephemeral search only: identity/scope changes hide old labels synchronously and abort late reads. */
export function useGlobalHeaderSearch({ actorId, workspaceId, query, enabled }: Options) {
  const [revision, setRevision] = useState(0);
  const [warmRevision, setWarmRevision] = useState(0);
  const warmController = useRef<AbortController | null>(null);
  const warmPromise = useRef<Promise<void> | null>(null);
  const warmGeneration = useRef(-1);
  const hintController = useRef<AbortController | null>(null);
  const key = JSON.stringify([actorId, workspaceId, query.trim(), enabled, revision]);
  const latestKey = useRef(key);
  latestKey.current = key;
  const requestController = useRef<AbortController | null>(null);
  const selectionController = useRef<AbortController | null>(null);
  const [state, setState] = useState<State | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  const refreshVisible = useCallback(() => {
    hintController.current?.abort();
    requestController.current?.abort();
    selectionController.current?.abort();
    // Invalidate the in-flight closure immediately, before React renders the revision.
    latestKey.current = '';
    setState(null);
    setRevision(value => value + 1);
  }, []);
  const refresh = useCallback(() => {
    invalidateGlobalSearchCandidates();
    warmController.current?.abort();
    setWarmRevision(value => value + 1);
    refreshVisible();
  }, [refreshVisible]);
  useEffect(() => {
    const generation = invalidateGlobalSearchCandidates();
    warmGeneration.current = generation;
    warmController.current?.abort();
    if (!actorId || !workspaceId) { warmPromise.current = null; return; }
    const controller = new AbortController();
    warmController.current = controller;
    // Desktop mount warms IDs even while the dropdown is closed. Failures never affect canonical search.
    warmPromise.current = warmProjectSearchCandidates({ actorId, workspaceId, query: '', signal: controller.signal }, generation).catch(() => {});
    return () => { controller.abort(); invalidateGlobalSearchCandidates(); };
  }, [actorId, workspaceId, warmRevision]);
  useEffect(() => {
    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);
    window.addEventListener(WORKSPACE_NAVIGATION_CHANGED_EVENT, refresh);
    window.addEventListener('quantis:auth-cleared', refresh);
    const unsubscribe = actorId ? subscribeToNavigationChanges(actorId, refresh, refresh) : () => {};
    return () => {
      window.removeEventListener('focus', refresh);
      window.removeEventListener('online', refresh);
      window.removeEventListener(WORKSPACE_NAVIGATION_CHANGED_EVENT, refresh);
      window.removeEventListener('quantis:auth-cleared', refresh);
      unsubscribe();
    };
  }, [actorId, refresh]);
  useEffect(() => {
    if (!enabled || !query.trim()) return;
    // Not every membership/capability source broadcasts yet; bound idle permission staleness.
    const interval = window.setInterval(refreshVisible, 15_000);
    return () => window.clearInterval(interval);
  }, [enabled, query, refreshVisible]);
  useEffect(() => {
    requestController.current?.abort();
    selectionController.current?.abort();
    if (!enabled || !actorId || !workspaceId || !query.trim()) { setState(null); return; }
    const controller = new AbortController();
    requestController.current = controller;
    setState({ key, page: null, loading: true, error: null });
    const timer = window.setTimeout(() => {
      let finished = false;
      let verifiedHints: GlobalSearchResult[] = [];
      let canonicalPartial: GlobalSearchPage | null = null;
      let canonicalProjectsPublished = false;
      const hints = new AbortController();
      hintController.current = hints;
      // Canonical providers always run, independently of catalogue availability or completeness.
      void searchGlobal({ actorId, workspaceId, query, signal: controller.signal, onProgress: page => {
        canonicalPartial = page;
        if (page.cursors.project > 0) { canonicalProjectsPublished = true; verifiedHints = []; }
        if (!controller.signal.aborted && latestKey.current === key) {
          const merged = { ...page, results: [...new Map([...verifiedHints, ...page.results].map(result => [`${result.type}:${result.id}`, result])).values()] };
          setState({ key, page: merged, loading: true, error: null });
        }
      } }).then(page => {
        finished = true; hints.abort();
        if (!controller.signal.aborted && latestKey.current === key) setState({ key, page, loading: false, error: page.unavailable.length ? unavailableMessage : null });
      }).catch(() => {
        finished = true; hints.abort();
        if (!controller.signal.aborted && latestKey.current === key) setState({ key, page: null, loading: false, error: failedMessage });
      });
      // An expired hint catalogue is re-warmed; idle permission refreshes retain only untrusted IDs.
      getGlobalSearchCandidateIds(actorId, workspaceId, query);
      if (warmGeneration.current !== currentGlobalSearchCandidateGeneration()) {
        warmController.current?.abort();
        const warm = new AbortController();
        warmController.current = warm;
        warmGeneration.current = currentGlobalSearchCandidateGeneration();
        warmPromise.current = warmProjectSearchCandidates({ actorId, workspaceId, query: '', signal: warm.signal }, warmGeneration.current).catch(() => {});
      }
      const haveHints = getGlobalSearchCandidateIds(actorId, workspaceId, query).length
        || getGlobalSearchTaskCandidateIds(actorId, workspaceId, query).length;
      // Already learned IDs must not wait behind a broad recency warm read.
      void (haveHints ? Promise.resolve() : warmPromise.current || Promise.resolve()).then(async () => {
        if (finished || controller.signal.aborted || latestKey.current !== key) return;
        const ids = getGlobalSearchCandidateIds(actorId, workspaceId, query);
        const taskIds = getGlobalSearchTaskCandidateIds(actorId, workspaceId, query);
        if (!ids.length && !taskIds.length) return;
        const results = await resolveProjectSearchCandidateHints({ actorId, workspaceId, query, signal: hints.signal }, ids, taskIds);
        if (!results.length || canonicalProjectsPublished || finished || hints.signal.aborted || controller.signal.aborted || latestKey.current !== key) return;
        verifiedHints = results;
        const combined = [...new Map([...results, ...(canonicalPartial?.results || [])].map(result => [`${result.type}:${result.id}`, result])).values()];
        const page: GlobalSearchPage = canonicalPartial ? { ...canonicalPartial, results: combined } : { results: combined, cursors: { project: 0, task: 0 }, hasMore: { project: false, task: false }, unavailable: [] };
        setState({ key, page, loading: true, error: null });
      }).catch(() => {});
    }, 60);
    return () => { window.clearTimeout(timer); controller.abort(); hintController.current?.abort(); selectionController.current?.abort(); };
  }, [key, enabled, actorId, workspaceId, query]);
  const loadMore = useCallback((type: GlobalSearchType) => {
    const current = stateRef.current;
    if (!enabled || !actorId || !workspaceId || latestKey.current !== key || current?.key !== key || !current.page?.hasMore[type] || current.loading) return;
    const controller = new AbortController();
    requestController.current?.abort();
    requestController.current = controller;
    setState({ ...current, page: null, loading: true });
    void searchGlobal({ actorId, workspaceId, query, signal: controller.signal }, current.page.cursors, [type], current.page.results).then(page => {
      if (controller.signal.aborted || latestKey.current !== key) return;
      const merged: GlobalSearchPage = { ...page,
        results: page.results,
        hasMore: { ...current.page!.hasMore, [type]: page.hasMore[type] },
        unavailable: [...new Set([...current.page!.unavailable.filter(source => source !== type), ...page.unavailable])],
      };
      setState({ key, page: merged, loading: false, error: merged.unavailable.length ? unavailableMessage : null });
    }).catch(() => {
      if (!controller.signal.aborted && latestKey.current === key) setState({ key, page: null, loading: false, error: failedMessage });
    });
  }, [actorId, workspaceId, enabled, query, key]);
  const select = useCallback(async (result: GlobalSearchResult): Promise<{ url: string } | null> => {
    if (!enabled || !actorId || !workspaceId || latestKey.current !== key) return null;
    const controller = new AbortController();
    selectionController.current?.abort();
    selectionController.current = controller;
    try {
      const selection = await resolveGlobalSearchSelection({ actorId, workspaceId, query, signal: controller.signal }, result);
      if (controller.signal.aborted || latestKey.current !== key) return null;
      if (selection) return selection;
    } catch {
      if (controller.signal.aborted || latestKey.current !== key) return null;
    }
    // A deleted or revoked result must disappear; never retain its cached label or cached target.
    setState({ key, page: null, loading: false, error: 'This result is no longer available to you.' });
    return null;
  }, [actorId, workspaceId, enabled, query, key]);
  const current = state?.key === key && enabled && actorId && workspaceId && query.trim() ? state : null;
  return { results: current?.page?.results || [], loading: current?.loading || false, error: current?.error || null,
    hasMore: (current?.page?.hasMore || {}) as Partial<Record<GlobalSearchType, boolean>>, loadMore, select, refresh };
}
